import S, { DataSignal } from 's-js';
import * as _ from 'underscore';
import { IAjax } from 'lib/Ajax';
import TZDate from 'lib/TZDate';
import { AutoSavingDraftObject } from 'lib/autoSave';
import { CaseInsensitiveContains } from 'lib/diacriticals';
import { debounceSignal } from 'lib/debounceSignal';
import { Entrypoints } from 'core/app/entrypoints';
import { DomainSummary } from 'data/models/DomainSummary';
import { UserSummary } from 'data/models/UserSummary';
import { Organization } from 'data/models/Organization';
import { PCUser } from 'data/models/PCUser';
import { OrganizationsResponse, OrganizationsResponseData } from 'data/models/OrganizationsResponse';
import { PCCache } from 'data/PCCache';
import { OrganizationsCache } from './cache';
import { PersonProfilePopup, PersonProfilePopupParams } from 'portcall/personprofilepopup/model';
import { InviteUsers, InviteUsersParams } from 'portcall/inviteusers/model';
import { UserPermissions, UserPermissionsParams } from 'portcall/userpermissions/model';

export interface OrganizationViewModel {
    Model : Organization;
    IsMyOrg : () => boolean;
    Users : () => PCUserViewModel[];
    ShowConfirmDelete : DataSignal<boolean>;
    Delete : () => void;
}

export interface PCUserViewModel {
    Model : PCUser;
    ToggleProfile : () => void;
    ProfilePopup : () => PersonProfilePopup | null;
}

export function Organizations(
    ajax : IAjax,
    entrypoints : Entrypoints,
    user : () => UserSummary | null,
    domain : DomainSummary,
    cache : PCCache,
    queryCache : OrganizationsCache,
    personProfilePopupModel : (params : PersonProfilePopupParams) => PersonProfilePopup,
    inviteUsersModel : (params : InviteUsersParams) => InviteUsers,
    userPermissionsModel : (params : UserPermissionsParams) => UserPermissions
) {
    let model = queryCache.OrganizationsResponses.update1(null, {}),
        showActionsMenu = S.data(false),
        showUserPermissions = S.data(false),
        userPermissions = S(() => {
            if (showUserPermissions()) {
                showActionsMenu(false);
                return userPermissionsModel({ onHidePopup: () => showUserPermissions(false) });
            }
            return null;
        }),
        showInviteUsers = S.data(false),
        inviteUsers = S(() => {
            if (showInviteUsers()) {
                showActionsMenu(false);
                return inviteUsersModel({ onHidePopup: () => showInviteUsers(false) });
            }
            return null;
        }),
        showManageOrgs = S.data(false),
        request = ajax.Url<OrganizationsResponseData>(entrypoints.Organizations).Get(),
        searchFilter = S.data(''),
        debouncedFilter = debounceSignal(searchFilter),
        orgs = model.Organizations
            .filter(org => !debouncedFilter()
                || orgContainsSearchTerm(org)
                || org.Users().some(user => user.Active() && userContainsSearchTerm(user)))
            .sort((org1, org2) => {
                let org1Name = org1.Name().toLowerCase(), org2Name = org2.Name().toLowerCase();
                return org1Name < org2Name ? -1 : org1Name > org2Name ? 1 : 0
            })
            .map(OrganizationViewModel);

    S(() => {
        if (showManageOrgs()) showActionsMenu(false);
    });

    request.done(data => {
        queryCache.OrganizationsResponses.update1(request.Date, data);
    });

    return {
        Request: request,
        Model: model,
        Organizations: orgs,
        SearchFilter: searchFilter,
        ShowUserPermissions: showUserPermissions,
        UserPermissions: userPermissions,
        ShowInviteUsers: showInviteUsers,
        InviteUsers: inviteUsers,
        ShowActionsMenu: showActionsMenu,
        ToggleActionsMenu: () => showActionsMenu(!showActionsMenu()),
        ShowManageOrgs: showManageOrgs,
        Domain: domain
    };

    function OrganizationViewModel(organization : Organization) : OrganizationViewModel {
        let client = ajax.Url(() => organization.Url() || ''),
            showConfirmDelete = S.data(false),
            users = organization.Users
                .filter(user => user.Active() && (!debouncedFilter()
                    || orgContainsSearchTerm(organization)
                    || userContainsSearchTerm(user)))
                .sort((user1, user2) => {
                    let user1LastName = user1.LastName().toLowerCase(),
                        user2LastName = user2.LastName().toLowerCase();
                    if (user1LastName !== user2LastName) return user1LastName < user2LastName ? -1 : 1;
                    let user1FirstName = user1.FirstName().toLowerCase(),
                        user2FirstName = user2.FirstName().toLowerCase();
                    return user1FirstName < user2FirstName ? -1 : user1FirstName > user2FirstName ? 1 : 0;
                })
                .map(PCUserViewModel);

        return {
            Model: organization,
            IsMyOrg: () => !!(user() && user()!.OrganizationId() === organization.Id()),
            Users: users,
            ShowConfirmDelete: showConfirmDelete,
            Delete: Delete
        };

        function Delete() {
            organization.Active(false);
            client.Put(organization).done(function () {
                model.Organizations.remove(organization);
            });
        }
    }

    function PCUserViewModel(user : PCUser) : PCUserViewModel {
        let showProfile = S.data(false),
            profilePopup = S(() => showProfile() ? personProfilePopupModel({id: user.Id(), onHidePopup: () => showProfile(false) }) : null);

        return {
            Model: user,
            ToggleProfile: () => showProfile(!showProfile()),
            ProfilePopup: profilePopup
        };
    }

    function orgContainsSearchTerm(organization : Organization) {
        return CaseInsensitiveContains(organization.Name(), debouncedFilter());
    }

    function userContainsSearchTerm(user : PCUser) {
        return CaseInsensitiveContains(user.FullName(), debouncedFilter());
    }
}
export type Organizations = ReturnType<typeof Organizations>;


// WEBPACK FOOTER //
// ./apps/portcall/organizations/model.ts